<script lang="ts" context="module">
  export const socialCardQueryKey = (username?: string) =>
    username ? ['socialCard', username] : ['socialCard'];
</script>

<script lang="ts">
  import { client } from '$lib/api/Client';
  import { createQuery } from '@tanstack/svelte-query';
  import { toastCustom } from '$utils/toasts';
  import TwitterLogo from '$icons/logos/TwitterLogo.svelte';
  import CopyIcon from '$icons/CopyIcon.svelte';
  import DownloadIcon from '$icons/DownloadIcon.svelte';
  import { cn } from '$lib/utils/misc';
  import LoaderIcon from '$icons/loaders/LoaderIcon.svelte';
  import { getBreakoutTwitterShareUrl } from '$shared/util';

  export let modalMode = false;
  let isCopying = false;

  const socialCardQuery = createQuery({
    queryKey: socialCardQueryKey(),
    queryFn: () => client.hackathon.getSocialShareableImage(),
    staleTime: Infinity,
  });

  async function copyImageToClipboard() {
    if (!$socialCardQuery.isSuccess) {
      toastCustom({
        title: 'Image not available yet. Please try again.',
        position: 'bottom-center',
        negative: true,
      });
      return;
    }
    isCopying = true;
    try {
      const imageUrl = new URL($socialCardQuery.data.url);
      imageUrl.searchParams.set('_t', Date.now().toString());
      const imageUrlString = imageUrl.toString();

      // Fetching the image blob - Promise stored for Safari
      const imagePromise = fetch(imageUrlString).then(async (res) => {
        if (!res.ok) throw new Error(`Failed to fetch image: ${res.statusText}`);
        return await res.blob();
      });

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      if (navigator.clipboard?.write) {
        let clipboardItem;
        if (isSafari) {
          // Safari/iOS: Pass the promise directly
          clipboardItem = new ClipboardItem({ 'image/png': imagePromise });
        } else {
          // Other browsers: Await the blob first
          const blob = await imagePromise;
          clipboardItem = new ClipboardItem({ [blob.type]: blob });
        }
        await navigator.clipboard.write([clipboardItem]);
        toastCustom({ title: 'Image copied to clipboard!', position: 'bottom-center' });
      }
      // Fallback 1: Copy URL text using modern API
      else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(imageUrlString);
        toastCustom({ title: 'Image URL copied to clipboard!', position: 'bottom-center' });
      }
      // Fallback 2: Legacy execCommand text copy
      else {
        const ta = document.createElement('textarea');
        ta.value = imageUrlString;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        let success = false;
        try {
          success = document.execCommand('copy');
        } catch (e) {
          /* ignore */
        }
        document.body.removeChild(ta);
        if (success) {
          toastCustom({ title: 'Image URL copied to clipboard!', position: 'bottom-center' });
        } else {
          throw new Error('All copy methods failed.');
        }
      }
    } catch (err: unknown) {
      console.error('Copy image error:', err);
      let description = 'Please try again or download the image.';
      if (err instanceof Error && err.message === 'All copy methods failed.') {
        description = 'Could not copy image or URL. Please download instead.';
      }
      // Avoid showing error for user-cancelled copy/paste prompts if detectable
      if (
        err instanceof DOMException &&
        (err.name === 'AbortError' || err.name === 'NotAllowedError')
      ) {
        // User likely cancelled the action or permission denied, don't show error toast
      } else {
        toastCustom({
          title: 'Failed to copy',
          description: description,
          position: 'bottom-center',
          negative: true,
        });
      }
    } finally {
      isCopying = false;
    }
  }

  function shareOnTwitter() {
    if ($socialCardQuery.isSuccess) {
      window.open(
        getBreakoutTwitterShareUrl(window.location.origin, $socialCardQuery.data.username),
        '_blank',
      );
    }
  }

  async function downloadImage() {
    if (!$socialCardQuery.isSuccess) {
      toastCustom({
        title: 'Image not available yet. Please try again.',
        position: 'bottom-center',
        negative: true,
      });
      return;
    }
    try {
      const imageUrl = new URL($socialCardQuery.data.url);
      imageUrl.searchParams.set('_t', Date.now().toString());
      const res = await fetch(imageUrl.toString());
      const blob = await res.blob();
      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `breakout-${$socialCardQuery.data.username || 'image'}.png`,
          types: [{ description: 'PNG Image', accept: { 'image/png': ['.png'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `breakout-${$socialCardQuery.data.username || 'image'}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
      }
      toastCustom({ title: 'Download saved', position: 'bottom-center' });
    } catch (error: unknown) {
      // ignore user cancellation (AbortError)
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error(error);
      toastCustom({
        title: 'Download failed',
        description: 'Please try again.',
        position: 'bottom-center',
        negative: true,
      });
    }
  }
</script>

{#if $socialCardQuery.isSuccess}
  <div
    class={cn('border-gray-dark-3 bg-gray-dark-2 overflow-hidden rounded border', $$props.class)}
  >
    {#if !modalMode}
      <div class="bg-gray-dark-4 flex items-center justify-between gap-2 px-3 py-2">
        <span class="text-gray-dark-12 font-architekt text-[10px] font-medium leading-tight"
          >Let others know you've entered the arena!</span
        >
        <div class="flex gap-1.5">
          <button
            class="bg-gray-dark-5 hover:bg-gray-dark-6 text-gray-dark-12 rounded p-1.5 transition-colors"
            title="Share on X"
            on:click={shareOnTwitter}
          >
            <TwitterLogo class="h-3 w-3 text-white" />
          </button>
          <button
            class="bg-gray-dark-5 hover:bg-gray-dark-6 text-gray-dark-12 rounded p-1.5 transition-colors"
            title="Copy image"
            on:click={copyImageToClipboard}
          >
            {#if isCopying}
              <LoaderIcon class="h-3 w-3 animate-spin text-white" />
            {:else}
              <CopyIcon class="h-3 w-3 text-white" />
            {/if}
          </button>
          <button
            class="bg-gray-dark-5 hover:bg-gray-dark-6 text-gray-dark-12 rounded p-1.5 transition-colors"
            title="Download image"
            on:click={downloadImage}
          >
            <DownloadIcon class="h-3 w-3 text-white" />
          </button>
        </div>
      </div>
    {/if}
    <button
      class="group relative block cursor-pointer border-none bg-transparent p-0"
      on:click={copyImageToClipboard}
      title="Copy image"
    >
      <img
        src={$socialCardQuery.data.url}
        alt="Social Shareable Card"
        class="block h-full w-full"
      />
      {#if !modalMode}
        <div
          class="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
        >
          {#if isCopying}
            <LoaderIcon class="h-12 w-12 animate-spin text-white" />
          {:else}
            <CopyIcon class="h-12 w-12 text-white" />
          {/if}
        </div>
      {/if}
    </button>
    {#if modalMode}
      <div class="bg-gray-dark-2 grid grid-cols-2 gap-3 p-3 sm:grid-cols-3">
        <button
          class="bg-gray-dark-5 hover:bg-gray-dark-6 text-gray-dark-12 flex items-center justify-center gap-2 rounded p-3 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          title="Copy image"
          on:click={copyImageToClipboard}
        >
          <span class="text-sm">Copy</span>
          {#if isCopying}
            <LoaderIcon class="h-4 w-4 animate-spin text-white" />
          {:else}
            <CopyIcon class="h-4 w-4 text-white" />
          {/if}
        </button>
        <button
          class="bg-gray-dark-5 hover:bg-gray-dark-6 text-gray-dark-12 flex items-center justify-center gap-2 rounded p-3 font-medium transition-colors"
          title="Download image"
          on:click={downloadImage}
        >
          <span class="text-sm">Download</span>
          <DownloadIcon class="h-4 w-4 text-white" />
        </button>
        <button
          class="bg-gray-dark-5 hover:bg-gray-dark-6 text-gray-dark-12 col-span-2 flex items-center justify-center gap-2 rounded p-3 font-medium transition-colors sm:col-span-1"
          title="Share on X"
          on:click={shareOnTwitter}
        >
          <span class="text-sm">Share on</span>
          <TwitterLogo class="h-4 w-4 text-white" />
        </button>
      </div>
    {/if}
  </div>
{/if}
